import { Banner } from "@/components/flexkit/Banner";
import { Link } from "@/components/flexkit/Link";
import { NotAuthorized } from "@/components/NotAuthorized";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { Text } from "@/components/podkit/typography/Text";
import { useDocumentTitle } from "@/hooks/use-document-title";

import { useMembership } from "@/hooks/use-membership";
import { useOrganization } from "@/queries/organization-queries";
import { AutoDeleteArchivedEnvironments } from "@/routes/settings/organization/policies/AutoDeleteArchivedEnvironments";
import { AvailableEditors } from "@/routes/settings/organization/policies/AvailableEditors";
import { DefaultImage } from "@/routes/settings/organization/policies/DefaultImage";
import { EnvironmentCreationRole } from "@/routes/settings/organization/policies/EnvironmentCreationRole";
import { GitpodDesktop } from "@/routes/settings/organization/policies/GitpodDesktop";
import { MaximumEnvironmentLifetime } from "@/routes/settings/organization/policies/MaximumEnvironmentLifetime";
import {
    MaximumRunningEnvironments,
    MaximumTotalEnvironments,
} from "@/routes/settings/organization/policies/MaximumEnvironments";
import { MaximumTimeout } from "@/routes/settings/organization/policies/MaximumTimeout";
import { PortSharing } from "@/routes/settings/organization/policies/PortSharing";
import { SecurityAgents } from "@/routes/settings/organization/policies/SecurityAgents";
import { OrganizationRole, OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import type { FC } from "react";

export const OrganizationPoliciesPage: FC = () => {
    useDocumentTitle("Policies");

    const { membership, isPending: isLoadingMembership } = useMembership();
    const { data: organization, isPending: isLoadingOrganization } = useOrganization();
    if (isLoadingMembership || !membership || isLoadingOrganization || !organization) {
        return <LoadingState />;
    }

    if (membership.userRole !== OrganizationRole.ADMIN) {
        return <NotAuthorized />;
    }

    const isEnterpriseTier = organization.tier === OrganizationTier.ENTERPRISE;
    const isCoreTier = organization.tier === OrganizationTier.CORE;

    return (
        <div className="flex max-w-[46rem] flex-col gap-4" data-testid="organization-policies-page">
            <Text className="text-base text-content-secondary">Configure organization-wide policies.</Text>

            {!isEnterpriseTier && (
                <Banner
                    variant="info"
                    className="py-4"
                    data-testid="free-tier-banner"
                    text={
                        <>
                            Upgrade to{" "}
                            <Link
                                to="/settings/manage-organization"
                                className="font-medium text-content-brand hover:underline"
                            >
                                Enterprise tier
                            </Link>{" "}
                            to manage policies and unlock more features.
                        </>
                    }
                />
            )}

            <GitpodDesktop editable={isEnterpriseTier} />
            <AvailableEditors editable={isEnterpriseTier} />

            <div className="flex flex-col gap-4">
                <h2 className="text-xl font-semibold">Environment policies</h2>

                <MaximumTimeout editable={isCoreTier || isEnterpriseTier} />

                <div className={`grid grid-cols-1 gap-4 md:grid-cols-2`}>
                    <MaximumEnvironmentLifetime editable={isEnterpriseTier} />
                    <AutoDeleteArchivedEnvironments editable={isEnterpriseTier} />
                </div>

                <EnvironmentCreationRole editable={isEnterpriseTier} />

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <MaximumTotalEnvironments editable={isEnterpriseTier} />
                    <MaximumRunningEnvironments editable={isEnterpriseTier} />
                </div>

                <DefaultImage editable={isEnterpriseTier} />
                <PortSharing editable={isEnterpriseTier} />
            </div>

            <div className="flex flex-col gap-4">
                <h2 className="text-xl font-semibold">Security Agents</h2>
                <SecurityAgents editable={isEnterpriseTier} />
            </div>
        </div>
    );
};
